import { useState, useEffect } from 'react';
import { Field, useFormikContext } from 'formik';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Tab,
  Tabs,
  Avatar,
  IconButton,
  useTheme,
  Stack,
} from '@mui/material';
import { Button } from '../../../../shared/components/ui/Button';
import { useAuth } from '../../../auth';
import { USER_ROLES, } from '../../constants/userConstants';
import { useTenant } from '../../../../shared/context/TenantContext';
import { useNavigate } from 'react-router-dom';




interface UserFormProps {
  mode: 'create' | 'edit';
  loading?: boolean;
  onCancel?: () => void;
  userId?: string;
}


const MaskedField = ({ mask, field, form, ...props }: any) => {
  const formatValue = (value: string) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');

    if (mask === '(99) 99999-9999') {
      if (numbers.length <= 2) return numbers;
      if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }

    if (mask === '999.999.999-99') {
      if (numbers.length <= 3) return numbers;
      if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
      if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
      return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
    }

    return value;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const formattedValue = formatValue(rawValue);

    if (field && field.onChange) {
      field.onChange({
        target: {
          name: field.name,
          value: formattedValue,
        },
      });
    } else if (props.onChange) {
      props.onChange({
        target: {
          name: props.name,
          value: formattedValue,
        },
      });
    }
  };

  return (
    <TextField
      {...field}
      {...props}
      onChange={handleChange}
      value={field?.value ?? props.value ?? ''}
    />
  );
};

export default function UserForm({
  mode,
  loading = false,
  onCancel,
  userId
}: UserFormProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const { selectedTenant, handleSetSelectedTenant } = useTenant();
  const [tab, setTab] = useState(0);
  const [avatar, setAvatar] = useState<string | null>(null);

  const { values, errors, touched, setFieldValue, isValid } = useFormikContext<any>();


  useEffect(() => {
    if (values.imageUrl && !avatar) {
      setAvatar(values.imageUrl);
    }
    if (values.tenantId && !selectedTenant) {
      handleSetSelectedTenant(values.tenantId);
    }
  }, [values.imageUrl, values.tenantId, avatar, selectedTenant, handleSetSelectedTenant]);



  const handleTabChange = (_: any, newValue: number) => setTab(newValue);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const url = URL.createObjectURL(file);
      setAvatar(url);
      setFieldValue('avatarFile', file);
      setFieldValue('imageUrl', url);
    }
  };






  return (
    <Box sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Field
            as={TextField}
            fullWidth
            label="Nome completo"
            name="name"
            required
            disabled={loading}
            error={!!(touched.name && errors.name)}
            helperText={touched.name && errors.name}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Field
            as={TextField}
            fullWidth
            label="E-mail"
            name="email"
            type="email"
            required
            disabled={loading}
            error={!!(touched.email && errors.email)}
            helperText={touched.email && errors.email}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Field
            name="cpf_or_rg"
            required
            disabled={loading}
            error={!!(touched.cpf_or_rg && errors.cpf_or_rg)}
            helperText={touched.cpf_or_rg && errors.cpf_or_rg}
            as={MaskedField}
            mask="999.999.999-99"
            label="CPF/RG"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Field
            as={TextField}
            fullWidth
            label="Telefone"
            name="phone"
            required
            disabled={loading}
            error={!!(touched.phone && errors.phone)}
            helperText={touched.phone && errors.phone}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Field
            as={TextField}
            fullWidth
            label="Data de nascimento"
            name="birth_date"
            type="date"
            disabled={loading}
            error={!!(touched.birth_date && errors.birth_date)}
            helperText={touched.birth_date && errors.birth_date}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        {false && (
          <Grid item xs={12} sm={6}>
            <Field
              as={TextField}
              select
              fullWidth
              label="Tipo de usuário"
              name="role"
              required
              disabled={mode === 'create' || loading}
              error={!!(touched.role && errors.role)}
              helperText={touched.role && errors.role}
            >
              {USER_ROLES.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Field>
          </Grid>
        )}
        {mode === 'create' && (
          <Grid item xs={12} sm={6}>
            <Field
              as={TextField}
              fullWidth
              label="Senha"
              name="password"
              type="password"
              required
              disabled={loading}
              error={!!(touched.password && errors.password)}
              helperText={touched.password && errors.password}
            />
          </Grid>
        )}
      </Grid>
      <Box sx={{ mt: 4 }}>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate(`/users/${userId}/change-password`)}
          >
            Alterar senha
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': { bgcolor: theme.palette.primary.dark },
            }}
          >
            {mode === 'create' ? 'Criar usuário' : 'Salvar alterações'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}